/*
 * Services Section – Personal training and studio workouts
 * Two main service cards with photos from the studio
 * Botanical Wellness style
 */

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { User, Users, CheckCircle2 } from "lucide-react";

const STUDIO_CLASS = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/IMG_1947_ca18a09a.JPG";
const STUDIO_MATS = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/SHARONTRAININGONHERHOME_d4c04028.webp";

const services = [
  {
    icon: User,
    title: "אימון אישי",
    subtitle: "אחד על אחד, בהתאמה מלאה",
    image: STUDIO_MATS,
    features: [
      "תוכנית אימון מותאמת אישית",
      "ליווי צמוד ומקצועי",
      "אימון בבית או בסטודיו",
      "מתאים לכל גיל ורמת כושר",
      "דגש על בטיחות ונוחות",
    ],
  },
  {
    icon: Users,
    title: "אימונים בסטודיו",
    subtitle: "קבוצות קטנות, יחס אישי",
    image: STUDIO_CLASS,
    features: [
      "שיעורי פילאטיס מזרן",
      "אימון פונקציונלי קבוצתי",
      "קבוצות קטנות עד 12 משתתפות",
      "אווירה תומכת ומעודדת",
      "ציוד מקצועי ומגוון",
    ],
  },
];

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="services" className="relative py-24 md:py-36 bg-[#F5F0E8]">
      <div className="container relative z-10" ref={ref}>
        {/* Section title */}
        <motion.div
          initial={false}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className="text-[#E8A87C] text-sm font-semibold tracking-wider uppercase mb-3 block"
            style={{ fontFamily: "var(--font-body)" }}
          >
            מה מחכה לך
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B4332]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            האימונים שלי
          </h2>
          <div className="w-20 h-1 bg-[#E8A87C] mx-auto mt-6 rounded-full" />
        </motion.div>

        {/* Service cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={false}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.2, duration: 0.6 }}
              className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
            >
              {/* Image header */}
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1B4332]/60 to-transparent" />
                <div className="absolute bottom-6 right-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#E8A87C] rounded-xl flex items-center justify-center">
                      <service.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3
                      className="text-2xl font-bold"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-white/80 text-sm" style={{ fontFamily: "var(--font-body)" }}>
                    {service.subtitle}
                  </p>
                </div>
              </div>

              {/* Features list */}
              <div className="p-6 md:p-8">
                <ul className="space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2D5A3D] flex-shrink-0" />
                      <span
                        className="text-[#3D6B4F]"
                        style={{ fontFamily: "var(--font-body)" }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-6 w-full bg-[#2D5A3D] hover:bg-[#1B4332] text-white font-semibold py-3.5 rounded-full transition-all duration-300 hover:shadow-lg"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  לפרטים נוספים ותיאום
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
